import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EnvelopeSimple, ArrowRight } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { usePortalLang, setPortalLang } from '../hooks/usePortalLang';
import { PortalLanguageToggle } from '../components/PortalLanguageToggle';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PortalLoginPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = usePortalLang();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/portal/login`, { email: email.trim() });
      localStorage.setItem('portal_token', res.data.token);
      localStorage.setItem('portal_customer', JSON.stringify(res.data.customer));
      // Apply server-saved language only if user hasn't picked one on this device.
      const serverLang = res.data.customer?.preferred_language;
      if (serverLang && !localStorage.getItem('portal_lang')) {
        setPortalLang(serverLang);
      }
      navigate('/portal/dashboard');
    } catch (error) {
      const msg = error.response?.data?.detail || t('Login failed', 'Error al iniciar sesión');
      toast.error(msg === 'No account found with this email'
        ? t('No account found with this email', 'No se encontró cuenta con este correo')
        : msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="portal-login-page">
      {/* Left side - branding */}
      <div className="hidden lg:flex lg:w-1/2 ship-gradient items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="absolute border border-white/20 rounded-full" style={{
              width: `${200 + i * 120}px`, height: `${200 + i * 120}px`,
              top: '50%', left: '50%', transform: 'translate(-50%, -50%)'
            }} />
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center relative z-10"
        >
          <img src="/assets/trackmypackage-logo.png" alt="Track My Package" className="h-40 mx-auto mb-6" />
          <p className="text-white/80 text-lg">{t('Customer Portal', 'Portal de Cliente')}</p>
          <div className="flex items-center justify-center gap-3 mt-6 text-white/60">
            <span>Shipping & Logistics Solutions</span>
          </div>
        </motion.div>
      </div>

      {/* Right side - login form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gray-50 relative">
        <div className="absolute top-4 right-4">
          <PortalLanguageToggle variant="light" />
        </div>
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-full max-w-md"
        >
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <img src="/assets/trackmypackage-logo.png" alt="Track My Package" className="h-24 mx-auto mb-3" />
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Bebas Neue' }}>
              {t('CUSTOMER PORTAL', 'PORTAL DE CLIENTE')}
            </h2>
            <p className="text-gray-500 text-sm mb-6">
              {t('Enter your email to view your packages and invoices', 'Ingresa tu correo para ver tus paquetes y facturas')}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <div className="relative">
                  <EnvelopeSimple size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('you@email.com', 'correo@ejemplo.com')}
                    className="pl-10 h-12 rounded-xl text-base"
                    required
                    autoFocus
                    data-testid="portal-email-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 ship-gradient text-white rounded-xl text-base font-medium"
                data-testid="portal-login-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    {t('View my packages', 'Ver mis paquetes')}
                    <ArrowRight size={20} className="ml-2" />
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 pt-4 border-t text-center">
              <a href="/login" className="text-sm text-[#0077FF] hover:underline">
                {t('Admin login', 'Acceso admin')}
              </a>
            </div>
          </div>

          <p className="text-center text-xs text-gray-400 mt-6">
            Track My Package | info@trackmypackage.app
          </p>
        </motion.div>
      </div>
    </div>
  );
}
